/**
 * Synchronisierung einrichten, starten und abmelden (T-04).
 *
 * - Erstes Geraet (Server leer): Passwort festlegen, alle Bestandsdaten
 *   bekommen Uhren und werden hochgeladen.
 * - Weiteres Geraet: Passwort pruefen, dann entweder die Server-Daten
 *   uebernehmen (lokale Daten werden ersetzt - Standard, vermeidet doppelte
 *   Erststart-Bereiche) oder beides zusammenfuehren.
 *
 * Bestandsdaten bekommen beim Einrichten die aelteste moegliche Uhr
 * (`uhrText(0, 0, geraet)`): Jede echte Bearbeitung ist neuer, und bei
 * gleichen Eintraegen auf beiden Geraeten entscheidet das Geraetekuerzel
 * auf beiden Seiten gleich.
 */
import type { OlePrivatDB } from '@/db/schema';
import { alsUebernahme } from '@/db/sync/erfassung';
import { syncSchluessel, type SyncFeldStand } from '@/domain/sync/typen';
import { uhrText, Uhrwerk, type Uhr } from '@/domain/sync/uhr';
import { vonB64u } from '@/lib/utils/base64url';
import { SyncApi, SyncFehler } from '@/lib/sync/api';
import { META, type SyncKontext } from '@/lib/sync/abgleich';
import { neuesSalz, passwortPasst, pruefwertErzeugen, schluesselAbleiten, STANDARD_RUNDEN } from '@/lib/sync/krypto';

export interface SyncKonfig {
  kennung: string;
  geraet: string;
  geraetName: string;
  eingerichtet: string;
}

export type Modus = 'uebernehmen' | 'zusammenfuehren';

const neuesGeraet = () => crypto.randomUUID().replace(/-/g, '').slice(0, 8);

/** Alle Bestandsdaten ohne Uhr bekommen eine - und sind damit offen. */
export async function alleMarkieren(db: OlePrivatDB, geraet: string): Promise<number> {
  const uralt: Uhr = uhrText(0, 0, geraet);
  let n = 0;
  for (const tabelle of db.syncTables.map((t) => db.table<Record<string, unknown>, string>(t.name))) {
    const vorhanden = new Set((await db.syncFelder.where('tabelle').equals(tabelle.name).primaryKeys()) as string[]);
    const neu: SyncFeldStand[] = [];
    await tabelle.toCollection().each((zeile) => {
      const id = String(zeile.id);
      const schluessel = syncSchluessel(tabelle.name, id);
      if (vorhanden.has(schluessel)) return;
      neu.push({ schluessel, tabelle: tabelle.name, id, f: Object.fromEntries(Object.keys(zeile).map((k) => [k, uralt])), bf: {}, seq: 0, offen: 1 });
    });
    await db.syncFelder.bulkPut(neu);
    n += neu.length;
  }
  return n;
}

/**
 * Lokale Nutzdaten leeren, ohne Grabsteine zu erzeugen - vor dem Uebernehmen.
 * Kopf und Zeilen des Papierkorbs gehen mit (sie kommen vom Server zurueck,
 * soweit es sie dort gibt). Die lokalen Bytes geloeschter Anhaenge
 * (`papierkorbDateien`) bleiben: Kommt ihr Eintrag vom Server zurueck, ist
 * der Anhang hier weiter wiederherstellbar; sonst entfernt `aufraeumen` sie.
 */
async function lokalLeeren(db: OlePrivatDB): Promise<void> {
  await db.transaction('rw', [...db.syncTables, db.syncFelder, db.syncKonflikte], async (tx) => {
    alsUebernahme(tx);
    for (const t of db.syncTables) await t.clear();
    await db.syncFelder.clear();
    await db.syncKonflikte.clear();
  });
}

async function speichern(db: OlePrivatDB, konfig: SyncKonfig, schluessel: CryptoKey): Promise<void> {
  await db.meta.bulkPut([
    { key: META.konfig, value: konfig },
    { key: META.schluessel, value: schluessel },
    { key: META.cursor, value: 0 },
  ]);
}

function aktivieren(db: OlePrivatDB, konfig: SyncKonfig, uhrStand?: Uhr): Uhrwerk {
  const uhr = new Uhrwerk(konfig.geraet, uhrStand);
  db.erfassung.uhr = uhr;
  db.erfassung.aktiv = true;
  return uhr;
}

export interface Einrichtung { kontext: SyncKontext; konfig: SyncKonfig }

/**
 * Einrichten. Ist der Server leer, wird dieses Geraet das erste und legt
 * den Schluessel an; sonst wird das Passwort geprueft und `modus` angewandt.
 */
export async function einrichten(db: OlePrivatDB, api: SyncApi, passwort: string, geraetName: string, modus: Modus = 'uebernehmen', runden = STANDARD_RUNDEN): Promise<Einrichtung> {
  if (passwort.length < 8) throw new SyncFehler('passwort', 'Das Sync-Passwort braucht mindestens 8 Zeichen');
  let info = await api.schluesselHolen();
  let erstes = false;
  let schluessel: CryptoKey;

  if (!info) {
    const salz = neuesSalz();
    schluessel = await schluesselAbleiten(passwort, vonB64u(salz), runden);
    const kennung = await api.schluesselAnlegen(salz, runden, await pruefwertErzeugen(schluessel));
    if (kennung) {
      erstes = true;
      info = { kennung, salz, runden, pruefwert: '' };
    } else {
      info = await api.schluesselHolen();
      if (!info) throw new SyncFehler('server', 'Server-Kopie nicht lesbar');
    }
  }
  if (!erstes) {
    schluessel = await schluesselAbleiten(passwort, vonB64u(info.salz), info.runden);
    if (!(await passwortPasst(schluessel, info.pruefwert))) throw new SyncFehler('passwort', 'Das Sync-Passwort stimmt nicht');
  }

  const konfig: SyncKonfig = { kennung: info.kennung, geraet: neuesGeraet(), geraetName: geraetName.trim() || 'Gerät', eingerichtet: new Date().toISOString() };
  api.kennung = konfig.kennung;
  if (!erstes && modus === 'uebernehmen') await lokalLeeren(db);
  else await db.syncFelder.clear();
  await speichern(db, konfig, schluessel!);
  const uhr = aktivieren(db, konfig);
  if (erstes || modus === 'zusammenfuehren') await alleMarkieren(db, konfig.geraet);
  return { kontext: { db, api, schluessel: schluessel!, uhr }, konfig };
}

/** Beim App-Start: eingerichtet? Dann Erfassung an und Kontext zurueck. */
export async function syncStarten(db: OlePrivatDB, api = new SyncApi()): Promise<Einrichtung | null> {
  const [konfig, schluessel, uhrStand] = await Promise.all([
    db.meta.get(META.konfig), db.meta.get(META.schluessel), db.meta.get(META.uhr),
  ]);
  if (!konfig || !(schluessel?.value instanceof CryptoKey)) return null;
  const k = konfig.value as SyncKonfig;
  api.kennung = k.kennung;
  const uhr = aktivieren(db, k, uhrStand?.value as Uhr | undefined);
  return { kontext: { db, api, schluessel: schluessel.value, uhr }, konfig: k };
}

/** Dieses Geraet abmelden. Die Daten bleiben, nur der Abgleich endet. */
export async function abmelden(db: OlePrivatDB): Promise<void> {
  db.erfassung.aktiv = false;
  db.erfassung.uhr = null;
  await db.meta.bulkDelete(Object.values(META));
  await db.syncFelder.clear();
  await db.syncKonflikte.clear();
}

/** Passwort vergessen: Server-Kopie loeschen und dieses Geraet abmelden. */
export async function serverZuruecksetzen(db: OlePrivatDB, api: SyncApi): Promise<void> {
  await api.allesLoeschen();
  await abmelden(db);
}
